import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface StoreWhatsAppConfig {
  /** Le bouton/app WhatsApp est-il actif pour cette boutique ? */
  enabled: boolean;
  /** Numéro nettoyé (chiffres uniquement) */
  number: string;
  /** Message d'accueil pré-rempli */
  message: string;
  /** Position du bouton flottant */
  position: 'right' | 'left';
  /** Chargement en cours */
  loading: boolean;
}

const DEFAULT_MESSAGE = 'Bonjour ! Je suis intéressé par vos produits.';

/** Nettoyage d'un numéro WhatsApp : on ne garde que les chiffres. */
export function cleanWhatsAppNumber(raw?: string | null): string {
  if (!raw) return '';
  return String(raw).replace(/[^0-9]/g, '');
}

/**
 * Construit une URL wa.me avec message pré-rempli.
 * Renvoie '#' si aucun numéro exploitable.
 */
export function buildWhatsAppUrl(number: string, message?: string): string {
  const clean = cleanWhatsAppNumber(number);
  if (!clean) return '#';
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${clean}${text}`;
}

/**
 * Lit la configuration WhatsApp de la boutique courante.
 *
 * La config est stockée dans `appvendeur.contenu` (JSON) pour l'app
 * « whatsapp-button » du marchand. Si l'app est active et qu'un numéro
 * est renseigné, le bouton s'affiche sur le site public. On retombe sur
 * le téléphone du commerce si aucune app n'est configurée mais qu'un
 * numéro de contact existe.
 */
export function useStoreWhatsApp(): StoreWhatsAppConfig {
  const { tenant, isTenant } = useTenant();
  const [config, setConfig] = useState<StoreWhatsAppConfig>({
    enabled: false,
    number: '',
    message: DEFAULT_MESSAGE,
    position: 'right',
    loading: true,
  });

  useEffect(() => {
    if (!isTenant || !tenant) {
      setConfig((c) => ({ ...c, loading: false }));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data } = await supabase
          .from('appvendeur')
          .select('contenu, status')
          .eq('idcommerce', tenant.id)
          .eq('nompage', 'whatsapp-button')
          .eq('status', 1)
          .maybeSingle();

        let number = '';
        let message = DEFAULT_MESSAGE;
        let position: 'right' | 'left' = 'right';

        if (data?.contenu) {
          try {
            const parsed = JSON.parse(data.contenu);
            number = cleanWhatsAppNumber(parsed?.number);
            if (parsed?.message) message = String(parsed.message);
            if (parsed?.position === 'left' || parsed?.position === 'right') {
              position = parsed.position;
            }
          } catch {
            /* contenu invalide : on ignore */
          }
        }

        // Repli : numéro de contact du commerce si aucun numéro d'app
        if (!number && tenant.telephone) {
          number = cleanWhatsAppNumber(tenant.telephone);
        }

        if (!cancelled) {
          setConfig({
            enabled: Boolean(number),
            number,
            message,
            position,
            loading: false,
          });
        }
      } catch {
        if (!cancelled) {
          const fallback = cleanWhatsAppNumber(tenant.telephone);
          setConfig({
            enabled: Boolean(fallback),
            number: fallback,
            message: DEFAULT_MESSAGE,
            position: 'right',
            loading: false,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isTenant, tenant]);

  return config;
}